import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Check, Download, ListMusic, PictureInPicture2, Share2 } from 'lucide-react'
import api from 'api/api'
import { AnswerShareTray } from 'components/answers/AnswerControls'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import { cacheAnswer } from 'state/cache'
import { getAnswerExternalShareUrl } from 'utils/shareUtils'
import AudioArtworkCard from './AudioArtworkCard'
import AudioAttachmentPlayer from 'components/answers/FileEmbed/AudioAttachmentPlayer'
import { PayloadTileMenu, type PayloadMenuAction } from 'components/answers/Answer/PayloadContainer'
import { playTrackInDock, toggleTrackInPlaylist } from 'components/shared/MediaDock/mediaDockEvents'
import { PLAY_IN_DOCK_LABEL } from 'components/shared/MediaDock/mediaDockPlayInDock'
import {
    playlistMenuLabel,
    useMediaKeyInPlaylist,
} from 'components/shared/MediaDock/useMediaKeyInPlaylist'
import { registerMediaTrack } from 'state/mediaPlaybackCoordinator'
import { hostedNativeMediaEnabled } from 'lib/hostedMediaPlayback'
import styles from 'components/answers/FileEmbed/ordinal-audio-card.module.scss'

interface OrdinalAudioCardProps {
    src: string
    /** Playback-coordinator key shared with the card's dock registration. */
    mediaKey?: string | null
    /**
     * The track kind registered with a custom transport. A radio song can be
     * a native video: its card shows the poster and the dock plays the picture.
     */
    kind?: 'audio' | 'video'
    title?: string
    previewUrl?: string
    answerId?: string
    /** Placeholder palette: orange in the ordinals stream, purple inside thread streams. */
    palette?: 'purple' | 'orange'
    /**
     * Replaces the file player in the card's transport strip. The radio's ON
     * AIR card puts its tuner there: the station has one clock for everyone,
     * so that card never mounts a player of its own.
     */
    transport?: React.ReactNode
    /** Passed to the artwork stage, centered over the image. */
    overlay?: React.ReactNode
    /** Top-left corner over the artwork (a music card's poster avatar). */
    topLeft?: React.ReactNode
    /** Controls right of the player's playlist button (a music card's upvalue). */
    playerActions?: React.ReactNode
    /** Controls at the left of the player's bottom row (a music card's total). */
    playerLeading?: React.ReactNode
    /** Drawn over the artwork, under the scrim and transport (a video song playing here). */
    stage?: React.ReactNode
    /** The dock's chrome layer while its video is parked over the card. */
    chromeLayer?: Element | null
    /**
     * Keep the track registered with the media dock. The regular player also
     * registers this card as its playback origin; a display-only custom
     * transport registers metadata only. Off for plain ordinal cards, which
     * the dock leaves alone.
     */
    persistPlayback?: boolean
    /**
     * Offer Download. The file leaves the app only by its owner's hand, so
     * hosts pass whether the viewer owns the artifact (or the song's post).
     */
    canDownload?: boolean
    /** Offer Share for the post behind the card (music cards), via the post's share tray. */
    canShare?: boolean
}

/**
 * Audio ordinals dressed like the combined image+audio payload card: an
 * artwork stage (the ordinal's preview image when one exists, the fixed
 * placeholder otherwise) with the title and styled transport docked on the
 * bottom scrim. The media menu retains the playlist action, and Download for
 * the owner, with host-specific offsets to clear the artifact menu.
 */
export default function OrdinalAudioCard({
    src,
    mediaKey,
    kind = 'audio',
    title,
    previewUrl,
    answerId,
    palette = 'purple',
    transport,
    overlay,
    topLeft,
    playerActions,
    playerLeading,
    stage,
    chromeLayer,
    persistPlayback = false,
    canDownload = false,
    canShare = false,
}: OrdinalAudioCardProps) {
    const hasCustomTransport = Boolean(transport)
    // A video song with the regular player: with hosted playback the dock's own
    // video is parked over the artwork (this stage) and the card's chrome moves
    // into the dock's layer above it, so it plays in place and glides to the dock.
    const hostedVideo = kind === 'video' && !hasCustomTransport && hostedNativeMediaEnabled()
    const stageRef = useRef<HTMLDivElement>(null)
    const [presentation, setPresentation] = useState<{
        isHostedHere: boolean
        chromeLayer: Element | null
    }>({ isHostedHere: false, chromeLayer: null })

    // AudioAttachmentPlayer normally performs this registration. A custom
    // transport replaces that player, but the card's Add to playlist action
    // still needs the same track metadata. Do not register an origin: a
    // display-only transport has no local playback runtime for the dock to
    // hand playback back to.
    useEffect(() => {
        if (!hasCustomTransport || !persistPlayback || !mediaKey || !src) return
        registerMediaTrack(mediaKey, {
            kind,
            src,
            title: title || (kind === 'video' ? 'Video' : 'Audio'),
            posterSrc: previewUrl || null,
            answerId: answerId || null,
        })
    }, [hasCustomTransport, persistPlayback, mediaKey, kind, src, title, previewUrl, answerId])

    // Share opens the post's own share tray; the post loads on demand when the
    // card only knows its id.
    const cachedShareAnswer = useCachedAnswer(canShare && answerId ? String(answerId) : null)
    const [fetchedShareAnswer, setFetchedShareAnswer] = useState<any>(null)
    const shareAnswer = cachedShareAnswer || fetchedShareAnswer
    const [shareOpen, setShareOpen] = useState(false)

    const inPlaylist = useMediaKeyInPlaylist(mediaKey)
    const menuActions = useMemo<PayloadMenuAction[]>(() => {
        const actions: PayloadMenuAction[] = []
        // Share leads the menu when the card has a post to share.
        if (canShare && answerId) {
            actions.push({
                label: 'Share',
                icon: <Share2 size={13} />,
                onClick: event => {
                    event.preventDefault()
                    event.stopPropagation()
                    if (shareAnswer?.id) {
                        setShareOpen(true)
                        return
                    }
                    void api
                        .getAnswer(answerId)
                        .then(answer => {
                            if (!answer?.id) return
                            cacheAnswer(answer)
                            setFetchedShareAnswer(answer)
                            setShareOpen(true)
                        })
                        .catch(() => {})
                },
            })
        }
        if (mediaKey) {
            actions.push({
                label: playlistMenuLabel(inPlaylist),
                icon: inPlaylist ? <Check size={13} /> : <ListMusic size={13} />,
                onClick: event => {
                    event.preventDefault()
                    event.stopPropagation()
                    toggleTrackInPlaylist(mediaKey, 'ordinal_audio_card_menu')
                },
            })
            actions.push({
                label: PLAY_IN_DOCK_LABEL,
                icon: <PictureInPicture2 size={13} />,
                onClick: event => {
                    event.preventDefault()
                    event.stopPropagation()
                    playTrackInDock(mediaKey, 'ordinal_audio_card_menu')
                },
            })
        }
        if (src && canDownload) {
            actions.push({
                label: 'Download',
                icon: <Download size={13} />,
                href: src,
                download: true,
                downloadName: title || 'ordinal-audio',
            })
        }
        return actions
    }, [answerId, canDownload, canShare, inPlaylist, mediaKey, shareAnswer, src, title])

    const card = (
        <AudioArtworkCard
            title={title}
            previewUrl={previewUrl}
            palette={palette}
            testId="ordinal-audio-card"
            overlay={overlay}
            topLeft={topLeft}
            stage={
                hostedVideo ? (
                    <div
                        ref={stageRef}
                        className={styles.videoStage}
                        data-hosted-media-fit="cover"
                    />
                ) : (
                    stage
                )
            }
            chromeLayer={
                hostedVideo && presentation.isHostedHere ? presentation.chromeLayer : chromeLayer
            }
            movableChrome={hostedVideo || chromeLayer !== undefined}
            titleTestId="ordinal-audio-title"
            topControls={
                menuActions.length > 0 && (
                    <PayloadTileMenu actions={menuActions} kind="audio" ariaLabel="Audio actions" />
                )
            }
        >
            {transport ? (
                <div className={styles.transport} data-testid="ordinal-audio-transport-slot">
                    {transport}
                </div>
            ) : (
                <AudioAttachmentPlayer
                    trailingActions={playerActions}
                    leadingActions={playerLeading}
                    mediaKind={kind}
                    originRef={hostedVideo ? stageRef : undefined}
                    onHostedPresentation={hostedVideo ? setPresentation : undefined}
                    src={src}
                    mediaKey={mediaKey || undefined}
                    fileName={title}
                    trackTitle={title}
                    posterSrc={previewUrl || undefined}
                    answerId={answerId}
                    audioSourceType={undefined}
                    persistPlayback={persistPlayback}
                    theme="scrim"
                    volumeFlyout
                    backgroundClickPassthrough
                    className={styles.transport}
                    isPaused={undefined}
                    resumePlaybackToken={undefined}
                    onDurationChange={undefined}
                    onProgressChange={undefined}
                    onPlaybackError={undefined}
                    onPlaybackStateChange={undefined}
                    onManualPlaybackChange={undefined}
                    onPlayIntent={undefined}
                    matchingBsvAttachment={undefined}
                    renderBsvAttachmentStatus={undefined}
                    showDelete={undefined}
                    onDelete={undefined}
                    statusOverlayClassName={undefined}
                    statusOverlayWithDeleteClassName={undefined}
                    deleteClassName={undefined}
                    stopPropagationOnly={undefined}
                    preventPropagation={undefined}
                />
            )}
        </AudioArtworkCard>
    )

    if (!canShare) return card

    return (
        <>
            {card}
            {shareAnswer?.id && (
                // The tray portals out of the card, but React still bubbles its
                // clicks here; stop them before a host's "open thread" surface.
                <span onClick={event => event.stopPropagation()}>
                    <AnswerShareTray
                        answer={shareAnswer}
                        copyContent={null}
                        isOpen={shareOpen}
                        onDismiss={() => setShareOpen(false)}
                        shareUrl={getAnswerExternalShareUrl(shareAnswer)}
                    />
                </span>
            )}
        </>
    )
}
